import type { UIMessageChunk } from "ai";

/**
 * Single-active-reader guard for a chat's UI message stream.
 *
 * The chat transport feeds BOTH the live send stream (`sendMessages`) and the
 * resume stream (`reconnectToStream`) into the *same* AI SDK `useChat` store,
 * which is keyed globally by `chatId`. If two readers attach to one chat at
 * once — a remount firing `resumeStream()` while the live send is still
 * draining, a bfcache restore reviving a reconnect, the SDK auto-reconnecting
 * mid-send — both replay the same `text-delta`s (the part id is stable, e.g.
 * `"0"`) into the one store. The deltas interleave/duplicate and the rendered
 * bubble comes out jumbled, while the durable server record (and therefore a
 * hard refresh) stays correct.
 *
 * `sanitizeChunkStream` only makes a *single* stream's replay idempotent — each
 * reader runs its own sanitizer with independent state, so it can't dedupe
 * across two concurrent readers. This registry closes that gap: at most one
 * reader is "active" per chat at any instant.
 *
 * Policy:
 *   - `send`  — user-initiated and authoritative. Always acquires; if another
 *               reader holds the slot it is aborted and the send takes over
 *               (mirrors the server's `supersedeActiveStream`).
 *   - `resume`— yields to an active `send` (suppressed, returns `null`) so the
 *               authoritative live stream keeps sole ownership. Over a stale
 *               `resume` it takes over instead: two resumes for one chat only
 *               arise across mounts (client-side navigation away from a running
 *               chat and back), where the incumbent belongs to a torn-down
 *               mount whose stream no longer feeds the visible store but whose
 *               lease survives in this module-level map. Suppressing the new
 *               mount's resume there left the in-progress turn blank until it
 *               completed. The swap is sequential (the incumbent is aborted
 *               before the new reader attaches), so the store is never
 *               double-fed. A stale *send* lease used to hit the same wall
 *               (suppression is only correct while the send actually feeds a
 *               live view); `useMagnus` now aborts its read on unmount /
 *               chat-switch, which releases the slot before the next mount's
 *               resume asks for it.
 *
 * The registry is module-level (one map per JS context) so it spans transport
 * instances — the double-consume we're guarding against happens precisely when
 * a *new* transport instance (a remount) races the old one.
 */

export type ReaderMode = "send" | "resume";

export interface ReaderLease {
  /**
   * Release the slot. Idempotent. Call on any stream termination — normal
   * close, error, or cancel — so a later reader can acquire. {@link
   * withLeaseRelease} wires this automatically.
   */
  release: () => void;
  /**
   * Abort signal scoped to this reader. Combines the caller's upstream signal
   * (the AI SDK's per-read `abortSignal`) with an internal controller that
   * fires when a later `send` supersedes this reader. Thread it through the
   * underlying fetch/iterator so a takeover actually tears the old read down.
   */
  signal: AbortSignal;
}

export interface ReaderConflict {
  chatId: string;
  /** Mode of the reader that already held the slot. */
  existingMode: ReaderMode;
  /** Mode of the reader that was just requested. */
  mode: ReaderMode;
  /** "aborted" when the incoming send took over; "suppressed" when a resume was dropped. */
  resolution: "aborted" | "suppressed";
}

export interface AcquireReaderOptions {
  chatId: string;
  mode: ReaderMode;
  /** Fired when acquiring conflicts with an existing reader (observability). */
  onConflict?: (conflict: ReaderConflict) => void;
  /** The AI SDK's own abort signal for this read, if any. */
  upstreamSignal?: AbortSignal;
}

interface ActiveReader {
  controller: AbortController;
  mode: ReaderMode;
}

const activeReaders = new Map<string, ActiveReader>();

/**
 * Combine an optional upstream signal with an internal controller signal into
 * one signal that aborts when *either* does. Prefers the native
 * `AbortSignal.any` (Node 20+/modern browsers/Hermes) and falls back to a
 * manual fan-in where it's unavailable.
 */
function combineSignals(
  upstream: AbortSignal | undefined,
  internal: AbortSignal
): AbortSignal {
  if (!upstream) {
    return internal;
  }
  const anyFn = (
    AbortSignal as unknown as {
      any?: (signals: AbortSignal[]) => AbortSignal;
    }
  ).any;
  if (typeof anyFn === "function") {
    return anyFn([upstream, internal]);
  }
  const controller = new AbortController();
  for (const signal of [upstream, internal]) {
    if (signal.aborted) {
      controller.abort(signal.reason);
      return controller.signal;
    }
    signal.addEventListener("abort", () => controller.abort(signal.reason), {
      once: true,
    });
  }
  return controller.signal;
}

/**
 * Acquire the single reader slot for `chatId`. Returns a {@link ReaderLease}
 * to thread into the stream, or `null` when a `resume` is suppressed because a
 * reader is already active. A `send` always returns a lease (taking over any
 * existing reader).
 */
export function acquireReaderSlot(
  options: AcquireReaderOptions
): ReaderLease | null {
  const { chatId, mode, upstreamSignal, onConflict } = options;
  const existing = activeReaders.get(chatId);

  if (existing) {
    // A resume yields only to an active *send*: the send is user-initiated,
    // authoritative, and already feeding the shared store, so re-resuming over
    // it would needlessly abort a healthy live stream and replay it from the
    // top.
    if (mode === "resume" && existing.mode === "send") {
      onConflict?.({
        chatId,
        mode,
        existingMode: existing.mode,
        resolution: "suppressed",
      });
      return null;
    }
    // Otherwise take over the slot, aborting the incumbent:
    //   - a new send supersedes anything (mirrors `supersedeActiveStream`);
    //   - a resume supersedes a *stale* resume. Two resumes for one chat only
    //     arise across mounts — navigating away from a running chat and back
    //     leaves the previous mount's reader lease in this module-level map,
    //     and its stream no longer feeds the now-remounted view. Suppressing
    //     the new mount's resume there left the in-progress turn blank until it
    //     completed. The swap is sequential (incumbent aborted before the new
    //     reader attaches), so the store is never double-fed.
    onConflict?.({
      chatId,
      mode,
      existingMode: existing.mode,
      resolution: "aborted",
    });
    existing.controller.abort(
      new DOMException(
        mode === "send"
          ? "Superseded by a new send"
          : "Superseded by a new resume",
        "AbortError"
      )
    );
    activeReaders.delete(chatId);
  }

  const controller = new AbortController();
  const entry: ActiveReader = { mode, controller };
  activeReaders.set(chatId, entry);

  let released = false;
  const release = () => {
    if (released) {
      return;
    }
    released = true;
    // Only clear the slot if it's still *this* reader — a later send may have
    // already replaced us, and we must not evict the new owner.
    if (activeReaders.get(chatId) === entry) {
      activeReaders.delete(chatId);
    }
  };

  // If the SDK aborts this read (unmount, stop, navigation), free the slot so
  // a legitimate reconnect afterwards isn't blocked by a stale entry.
  if (upstreamSignal) {
    if (upstreamSignal.aborted) {
      release();
    } else {
      upstreamSignal.addEventListener("abort", release, { once: true });
    }
  }

  return { signal: combineSignals(upstreamSignal, controller.signal), release };
}

/**
 * Abort whatever reader currently holds `chatId`'s slot (no-op when none).
 * Returns whether a reader was aborted.
 *
 * This exists for user-initiated stop on a *resumed* stream. The AI SDK's
 * `stop()` aborts its per-request `activeResponse.abortController`, but that
 * signal is only threaded into the transport on the send path
 * (`sendMessages({ abortSignal })`). `reconnectToStream` is invoked *before*
 * the controller even exists and receives no signal at all (current `ai`
 * versions pass only `{chatId, metadata, headers, body}`), so on a resumed
 * stream `stop()` merely flips the SDK's internal `isAbort` flag while the
 * reconnect fetch keeps streaming — the stop button visibly did nothing.
 * The lease's internal controller here IS wired into that fetch, so aborting
 * through the registry tears the read down for send and resume alike.
 */
export function abortActiveReader(chatId: string): boolean {
  const existing = activeReaders.get(chatId);
  if (!existing) {
    return false;
  }
  existing.controller.abort(
    new DOMException("Stopped by the user", "AbortError")
  );
  activeReaders.delete(chatId);
  return true;
}

/**
 * Wrap a chunk stream so the lease is released on *any* termination — normal
 * close, error, or downstream cancel. Keeps slot bookkeeping correct without
 * the caller having to chase every exit path.
 */
export function withLeaseRelease(
  stream: ReadableStream<UIMessageChunk>,
  release: () => void
): ReadableStream<UIMessageChunk> {
  const reader = stream.getReader();
  return new ReadableStream<UIMessageChunk>({
    async pull(controller) {
      try {
        const { done, value } = await reader.read();
        if (done) {
          release();
          controller.close();
          return;
        }
        controller.enqueue(value);
      } catch (err) {
        release();
        controller.error(err);
      }
    },
    async cancel(reason) {
      release();
      await reader.cancel(reason).catch(() => {
        /* already closed */
      });
    },
  });
}

/**
 * Test-only: drop all tracked readers. Not exported from the package barrel.
 */
export function __resetReaderRegistryForTests(): void {
  activeReaders.clear();
}
